import { ConcurrencyLimiter } from "../request-concurrency-limiter";
import type { CollectionClient, MaterialRef } from "./collection-client.types";
import { SAVED_STATE_CONCURRENCY } from "./collections.constants";

/**
 * "Is this material in at least one of my collections?" — the filled-heart read
 * (AC7), kept apart from the popover's full list so it can be cached and
 * batched. Every visible result card asks once.
 *
 * This interface is the swap point for a batch saved-state endpoint: a
 * batching implementation (collect the refs asked for in one tick, one call)
 * replaces {@link createSavedStateStore} and nothing that calls it changes.
 */
export interface SavedStateStore {
  /**
   * Resolves whether the material is saved. Answers from the cache when it can;
   * rejects when the lookup fails (nothing is cached then, so the next call
   * retries).
   */
  get(material: MaterialRef): Promise<boolean>;
  /** Records the caller's own change (add / remove / a freshly loaded list). */
  set(material: MaterialRef, saved: boolean): void;
}

export interface SavedStateStoreOptions {
  /** Lookups allowed in flight at once. */
  concurrency?: number;
}

const keyOf = (material: MaterialRef) =>
  `${material.sourceDiscriminator}:${material.sourceRefId}`;

/**
 * Saved-state store over {@link CollectionClient.getMembership}: one lookup per
 * material, deduped while in flight, cached afterwards, and gated by a
 * concurrency limiter so a ~40-card results page doesn't burst ~40 requests at
 * the service at once (CX-3786).
 *
 * The cache lives as long as the page. That is per user only because Color
 * Finder reloads the page on both login and logout (`CovestroHeader`); if that
 * ever stops being true, clear the store on sign-out.
 *
 * @param client - Where lookups go.
 * @param options - Concurrency cap (default {@link SAVED_STATE_CONCURRENCY}).
 * @returns The store.
 */
export const createSavedStateStore = (
  client: CollectionClient,
  { concurrency = SAVED_STATE_CONCURRENCY }: SavedStateStoreOptions = {},
): SavedStateStore => {
  const limiter = new ConcurrencyLimiter(concurrency);
  const cache = new Map<string, boolean>();
  const inFlight = new Map<string, Promise<boolean>>();

  const lookup = async (material: MaterialRef, key: string) => {
    await limiter.acquire();
    try {
      const rows = await client.getMembership(material);
      // A `set` while this was in flight is newer than this answer; keep it.
      if (!cache.has(key)) {
        cache.set(
          key,
          rows.some((row) => row.contains),
        );
      }
      return cache.get(key) === true;
    } finally {
      limiter.release();
      inFlight.delete(key);
    }
  };

  return {
    get(material) {
      const key = keyOf(material);
      const cached = cache.get(key);
      if (cached !== undefined) {
        return Promise.resolve(cached);
      }
      let pending = inFlight.get(key);
      if (!pending) {
        pending = lookup(material, key);
        inFlight.set(key, pending);
      }
      return pending;
    },
    set(material, saved) {
      cache.set(keyOf(material), saved);
    },
  };
};

const stores = new WeakMap<CollectionClient, SavedStateStore>();

/**
 * The store for a client — one per client instance, so tests and stories that
 * inject their own client never share cached state with the app's.
 *
 * @param client - The client from `CollectionClientContext`.
 * @returns The same store for the same client.
 */
export const getSavedStateStore = (
  client: CollectionClient,
): SavedStateStore => {
  let store = stores.get(client);
  if (!store) {
    store = createSavedStateStore(client);
    stores.set(client, store);
  }
  return store;
};
